import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { INR } from "@/lib/api";
import { ArrowLeft, Download, Printer, Share2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import { amountInWords } from "@/lib/amountWords";

const LOGO_URL = "/atul-logo.png";

async function loadLogoDataURL() {
  try {
    const res = await fetch(LOGO_URL);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch (e) { return null; }
}

export default function Invoice() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    api.get(`/invoices/${vehicleId}`).then(({ data }) => setData(data));
  }, [vehicleId]);

  const downloadPDF = async () => {
    if (!data) return;
    const { dealer, vehicle, finance, invoice } = data;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();

    // Header
    doc.setFillColor(30, 58, 138);
    doc.rect(0, 0, W, 100, "F");

    // Logo — black rounded square, larger for crisp render
    const logoData = await loadLogoDataURL();
    if (logoData) {
      doc.setFillColor(0, 0, 0);
      doc.roundedRect(28, 18, 64, 64, 6, 6, "F");
      try {
        // Use a large intrinsic size for jsPDF to embed at higher effective resolution
        doc.addImage(logoData, "JPEG", 32, 22, 56, 56, undefined, "FAST");
      } catch (e) { /* ignore */ }
    }
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text(dealer.name, 110, 40);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(dealer.address, 110, 58);
    doc.text(`GSTIN: ${dealer.gstin}`, 110, 72);
    doc.text(`Ph: ${dealer.phone}   Email: ${dealer.email || ""}`, 110, 86);

    doc.setFillColor(249, 115, 22);
    doc.rect(W - 180, 20, 140, 60, "F");
    doc.setFontSize(9);
    doc.text("TAX INVOICE", W - 170, 38);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(invoice.invoice_number, W - 170, 56);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(new Date(invoice.invoice_date).toLocaleDateString("en-IN"), W - 170, 72);

    // Customer + Vehicle
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Bill To", 40, 140);
    doc.text("Vehicle", W / 2 + 20, 140);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    doc.text(finance.customer_name || "—", 40, 156);
    doc.text(finance.customer_phone || "", 40, 170);
    const addrLines = doc.splitTextToSize(finance.customer_address || "", 220);
    doc.text(addrLines, 40, 184);

    doc.text(`Model: ${vehicle.model}`, W / 2 + 20, 156);
    doc.text(`Chassis: ${vehicle.chassis_number}`, W / 2 + 20, 170);
    doc.text(`Engine: ${vehicle.engine_number}`, W / 2 + 20, 184);
    doc.text(`Color: ${vehicle.color}   Year: ${vehicle.manufacturing_year}`, W / 2 + 20, 198);
    doc.text(`Branch: ${vehicle.branch}`, W / 2 + 20, 212);

    // Price breakup
    autoTable(doc, {
      startY: 240,
      head: [["Description", "Amount (INR)"]],
      body: [
        ["Ex-Showroom Price", INR(finance.ex_showroom_price)],
        ["Insurance", INR(finance.insurance_price)],
        ["RTO Charges", INR(finance.rto_charges)],
        ["Permit Charges", INR(finance.permit_charges)],
        ["Earthing Charges", INR(finance.earthing_charges)],
        ...(Number(finance.file_charges || 0) > 0 ? [["File Charges", INR(finance.file_charges)]] : []),
        ...(Number(finance.discount || 0) > 0 ? [["Discount", "- " + INR(finance.discount)]] : []),
        [{ content: "Total Vehicle Price", styles: { fontStyle: "bold" } }, { content: INR(finance.total_vehicle_price), styles: { fontStyle: "bold" } }],
        ["Loan Amount", INR(finance.loan_amount)],
        [{ content: "Down Payment", styles: { fontStyle: "bold" } }, { content: INR(finance.down_payment), styles: { fontStyle: "bold" } }],
      ],
      headStyles: { fillColor: [30, 58, 138], textColor: 255 },
      styles: { fontSize: 10 },
      columnStyles: { 1: { halign: "right" } },
    });

    // Payments table
    if ((finance.payments || []).length) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 15,
        head: [["Date", "Mode", "Txn ID", "Remarks", "Amount"]],
        body: finance.payments.map(p => [p.date || "", p.mode, p.transaction_id || "—", p.remarks || "—", INR(p.amount)]),
        headStyles: { fillColor: [249, 115, 22], textColor: 255 },
        styles: { fontSize: 9 },
        columnStyles: { 4: { halign: "right" } },
      });
    }

    // Totals — centered on the page
    const yEnd = doc.lastAutoTable.finalY + 20;
    const centerX = W / 2;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text(`Total Paid: ${INR(finance.total_paid)}`, centerX, yEnd, { align: "center" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    const paidWords = doc.splitTextToSize(`(${amountInWords(finance.total_paid)})`, W - 80);
    doc.text(paidWords, centerX, yEnd + 14, { align: "center" });

    const rowGap = 12 + paidWords.length * 11;
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text(`Remaining Balance: ${INR(finance.remaining_balance)}`, centerX, yEnd + rowGap + 10, { align: "center" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    const remWords = doc.splitTextToSize(`(${amountInWords(finance.remaining_balance)})`, W - 80);
    doc.text(remWords, centerX, yEnd + rowGap + 24, { align: "center" });
    doc.setTextColor(15, 23, 42);

    const yAfterTotals = yEnd + rowGap + 24 + remWords.length * 11;

    // Signatures
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    doc.line(40, yAfterTotals + 60, 200, yAfterTotals + 60);
    doc.text("Customer Signature", 40, yAfterTotals + 75);
    doc.line(W - 200, yAfterTotals + 60, W - 40, yAfterTotals + 60);
    doc.text("For M/s India Atul Motors", W - 200, yAfterTotals + 75);

    doc.save(`${invoice.invoice_number}.pdf`);
  };

  const buildPDFBlob = async () => {
    if (!data) return null;
    // Reuse the same PDF-generation logic by producing a Blob instead of save
    const savedFn = jsPDF.prototype.save;
    let capturedBlob = null;
    jsPDF.prototype.save = function () {
      capturedBlob = this.output("blob");
      return this;
    };
    try {
      await downloadPDF();
    } finally {
      jsPDF.prototype.save = savedFn;
    }
    return capturedBlob;
  };

  const shareWhatsApp = async () => {
    if (!data) return;
    const { dealer, vehicle, finance, invoice } = data;
    const lines = [
      `*${dealer.name}* — Tax Invoice`,
      `Invoice: ${invoice.invoice_number}`,
      `Vehicle: ${vehicle.model}`,
      `Chassis: ${vehicle.chassis_number}`,
      `Total Vehicle Price: ₹${(finance.total_vehicle_price || 0).toLocaleString("en-IN")}`,
      `Loan Amount: ₹${(finance.loan_amount || 0).toLocaleString("en-IN")}`,
      `Down Payment: ₹${(finance.down_payment || 0).toLocaleString("en-IN")}`,
      `Total Paid: ₹${(finance.total_paid || 0).toLocaleString("en-IN")}`,
      `Remaining: ₹${(finance.remaining_balance || 0).toLocaleString("en-IN")}`,
      `Contact: ${dealer.phone}`,
    ].join("\n");

    // Try Web Share API with PDF attachment on supported (usually mobile) browsers
    const blob = await buildPDFBlob();
    if (blob && navigator.canShare) {
      const file = new File([blob], `${invoice.invoice_number}.pdf`, { type: "application/pdf" });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            title: `${dealer.name} — ${invoice.invoice_number}`,
            text: lines,
            files: [file],
          });
          return;
        } catch (e) { /* user cancelled or unsupported */ }
      }
    }

    // Fallback: open wa.me with the text summary
    const phoneDigits = (finance.customer_phone || "").replace(/\D/g, "");
    const encoded = encodeURIComponent(lines + "\n\n(PDF invoice downloaded to your device — please attach it here.)");
    const url = phoneDigits
      ? `https://wa.me/91${phoneDigits.slice(-10)}?text=${encoded}`
      : `https://wa.me/?text=${encoded}`;
    // Also trigger PDF download so user can attach manually
    downloadPDF();
    window.open(url, "_blank");
    toast.info("WhatsApp opened — PDF downloaded, attach it in the chat if needed");
  };

  if (!data) return <div className="text-slate-500 text-sm">Loading invoice…</div>;

  const { dealer, vehicle, finance, invoice } = data;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="invoice-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex gap-2">
          <button onClick={() => window.print()} data-testid="invoice-print"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm">
            <Printer className="w-4 h-4" /> Print
          </button>
          <button onClick={shareWhatsApp} data-testid="invoice-whatsapp"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm">
            <Share2 className="w-4 h-4" /> WhatsApp
          </button>
          <button onClick={downloadPDF} data-testid="invoice-download"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-orange bg-brand-orange-hover text-white text-sm">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </div>

      <div ref={printRef} className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-testid="invoice-preview">
        <div className="bg-blue-900 text-white p-8 flex items-start justify-between gap-6">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="w-24 h-24 rounded-lg bg-white flex items-center justify-center flex-shrink-0 overflow-hidden shadow-md p-2">
              <img src={LOGO_URL} alt="Atul Auto" className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <div className="font-heading text-2xl font-bold">{dealer.name}</div>
              <div className="text-sm text-blue-200 mt-1">{dealer.address}</div>
              <div className="text-xs text-blue-300 mt-2">
                GSTIN: {dealer.gstin} · Ph: {dealer.phone}
                {dealer.email && <> · {dealer.email}</>}
              </div>
            </div>
          </div>
          <div className="bg-orange-500 rounded-md p-4 min-w-[180px] text-right flex-shrink-0">
            <div className="text-xs uppercase tracking-widest">Tax Invoice</div>
            <div className="font-heading text-lg font-bold" data-testid="invoice-number">{invoice.invoice_number}</div>
            <div className="text-xs mt-1">{new Date(invoice.invoice_date).toLocaleDateString("en-IN")}</div>
          </div>
        </div>

        <div className="p-8 grid grid-cols-2 gap-6 border-b border-slate-200">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Bill To</div>
            <div className="font-medium text-slate-900">{finance.customer_name || "—"}</div>
            <div className="text-sm text-slate-600">{finance.customer_phone}</div>
            <div className="text-sm text-slate-600 mt-1">{finance.customer_address}</div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Vehicle</div>
            <div className="font-medium text-slate-900">{vehicle.model}</div>
            <div className="text-xs font-mono text-slate-600">Chassis: {vehicle.chassis_number}</div>
            <div className="text-xs font-mono text-slate-600">Engine: {vehicle.engine_number}</div>
            <div className="text-sm text-slate-600 mt-1">{vehicle.color} · {vehicle.manufacturing_year} · {vehicle.branch}</div>
          </div>
        </div>

        <div className="p-8 border-b border-slate-200">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Price Breakup</div>
          <table className="w-full">
            <tbody>
              {[
                ["Ex-Showroom Price", finance.ex_showroom_price],
                ["Insurance", finance.insurance_price],
                ["RTO Charges", finance.rto_charges],
                ["Permit Charges", finance.permit_charges],
                ["Earthing Charges", finance.earthing_charges],
                ...(Number(finance.file_charges || 0) > 0 ? [["File Charges", finance.file_charges]] : []),
              ].map(([k, v]) => (
                <tr key={k} className="border-b border-slate-100">
                  <td className="py-2 text-sm text-slate-700">{k}</td>
                  <td className="py-2 text-sm text-slate-900 text-right font-mono">{INR(v)}</td>
                </tr>
              ))}
              {Number(finance.discount || 0) > 0 && (
                <tr className="border-b border-slate-100">
                  <td className="py-2 text-sm text-emerald-700">Discount</td>
                  <td className="py-2 text-sm text-emerald-700 text-right font-mono">− {INR(finance.discount)}</td>
                </tr>
              )}
              <tr className="border-b border-slate-200 bg-slate-50">
                <td className="py-3 text-sm font-semibold text-slate-900">Total Vehicle Price</td>
                <td className="py-3 text-sm font-bold text-slate-900 text-right font-mono">{INR(finance.total_vehicle_price)}</td>
              </tr>
              <tr>
                <td className="py-2 text-sm text-slate-700">Loan Amount</td>
                <td className="py-2 text-sm text-slate-900 text-right font-mono">{INR(finance.loan_amount)}</td>
              </tr>
              <tr className="bg-orange-50">
                <td className="py-3 text-sm font-semibold text-orange-900">Down Payment</td>
                <td className="py-3 text-sm font-bold text-orange-900 text-right font-mono">{INR(finance.down_payment)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {(finance.payments || []).length > 0 && (
          <div className="p-8 border-b border-slate-200">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Payments</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Mode</th>
                  <th className="text-left py-2">Txn ID</th>
                  <th className="text-left py-2">Remarks</th>
                  <th className="text-right py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {finance.payments.map((p, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2">{p.date}</td>
                    <td className="py-2">{p.mode}</td>
                    <td className="py-2 font-mono text-xs">{p.transaction_id || "—"}</td>
                    <td className="py-2">{p.remarks || "—"}</td>
                    <td className="py-2 text-right font-mono">{INR(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="p-8 grid grid-cols-2 gap-6">
          <div className="bg-emerald-50 rounded-md p-4 text-center">
            <div className="text-xs uppercase tracking-widest text-emerald-700 mb-1">Total Paid</div>
            <div className="font-heading text-2xl font-bold text-emerald-900">{INR(finance.total_paid)}</div>
            <div className="text-[11px] italic text-emerald-800 mt-1" data-testid="invoice-paid-in-words">
              ({amountInWords(finance.total_paid)})
            </div>
          </div>
          <div className="bg-slate-100 rounded-md p-4 text-center">
            <div className="text-xs uppercase tracking-widest text-slate-600 mb-1">Remaining Balance</div>
            <div className="font-heading text-2xl font-bold text-slate-900">{INR(finance.remaining_balance)}</div>
            <div className="text-[11px] italic text-slate-700 mt-1" data-testid="invoice-remaining-in-words">
              ({amountInWords(finance.remaining_balance)})
            </div>
          </div>
        </div>

        <div className="p-8 pt-4 grid grid-cols-2 gap-6 border-t border-slate-200">
          <div>
            <div className="h-14 border-b border-slate-300" />
            <div className="text-xs text-slate-500 mt-1">Customer Signature</div>
          </div>
          <div>
            <div className="h-14 border-b border-slate-300" />
            <div className="text-xs text-slate-500 mt-1">For M/s India Atul Motors</div>
          </div>
        </div>
      </div>
    </div>
  );
}
